import React, { useEffect, useState, useRef } from 'react';
import { Mail, Save, ArrowLeft, RotateCcw, Send, CheckCircle, AlertTriangle, Code, Plus, Trash2 } from 'lucide-react';
import { useApi } from './DashboardLayout';
import TipTapEditor from '../components/TipTapEditor';
import { FeedbackBanner, PlaceholderBar } from '../components/SharedUI';

function CreateTemplateForm({ onCreated, onCancel, apiFetch }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('general');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleCreate = async () => {
    if (!name.trim()) { setError('Name erforderlich'); return; }
    setSaving(true); setError('');
    try {
      const r = await apiFetch('/api/settings/mail-templates', {
        method: 'POST', body: JSON.stringify({ name, description, category }),
      });
      if (!r) { setSaving(false); return; }
      const text = await r.text();
      const d = JSON.parse(text);
      if (d?.status) onCreated(d.template?.key);
      else setError(d?.detail || 'Fehler');
    } catch (e) { setError(e.message); }
    setSaving(false);
  };

  return (
    <div className="card" style={{ marginBottom: '1.5rem' }} data-testid="create-template-form">
      <div className="card-body">
        <h4 style={{ fontSize: 15, fontWeight: 700, marginBottom: 16 }}>Neue Vorlage erstellen</h4>
        {error && <div style={{ background: '#FEE2E2', color: '#991B1B', padding: '8px 12px', borderRadius: 8, fontSize: 13, marginBottom: 12 }}>{error}</div>}
        <div style={{ display: 'grid', gap: 12, maxWidth: 500 }}>
          <div>
            <label className="smtp-label">Name</label>
            <input className="form-input" value={name} onChange={e => setName(e.target.value)} placeholder="z.B. Einladung Jahresversammlung" data-testid="new-template-name" />
          </div>
          <div>
            <label className="smtp-label">Beschreibung</label>
            <input className="form-input" value={description} onChange={e => setDescription(e.target.value)} placeholder="Wofür wird diese Vorlage verwendet?" data-testid="new-template-description" />
          </div>
          <div>
            <label className="smtp-label">Kategorie</label>
            <select className="form-input" value={category} onChange={e => setCategory(e.target.value)} data-testid="new-template-category">
              <option value="general">Allgemein</option>
              <option value="system">System</option>
            </select>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
          <button className="btn btn-primary" onClick={handleCreate} disabled={saving} data-testid="new-template-create-btn">
            <Plus size={14} /> {saving ? 'Erstelle...' : 'Vorlage erstellen'}
          </button>
          <button className="btn" onClick={onCancel} style={{ background: 'var(--page-bg)', border: '1px solid var(--border)' }}>Abbrechen</button>
        </div>
      </div>
    </div>
  );
}

function TemplateList({ templates, onSelect, onRefresh, apiFetch }) {
  const [showCreate, setShowCreate] = useState(false);
  return (
    <div data-testid="mail-template-list">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h2>Einstellungen</h2>
          <p>E-Mail-Vorlagen</p>
        </div>
        {!showCreate && (
          <button className="btn btn-primary" onClick={() => setShowCreate(true)} data-testid="new-template-btn" style={{ fontSize: 13 }}>
            <Plus size={14} /> Neue Vorlage
          </button>
        )}
      </div>

      {showCreate && (
        <CreateTemplateForm
          apiFetch={apiFetch}
          onCreated={(key) => { setShowCreate(false); onSelect(key); }}
          onCancel={() => setShowCreate(false)}
        />
      )}
      <div className="card">
        <div className="table-wrap">
          <table data-testid="templates-table">
            <thead>
              <tr>
                <th>Vorlage</th>
                <th>Beschreibung</th>
                <th style={{ width: 100 }}>Kategorie</th>
                <th style={{ width: 120 }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {templates.map(t => (
                <tr key={t.key} onClick={() => onSelect(t.key)} style={{ cursor: 'pointer' }} data-testid={`template-row-${t.key}`}>
                  <td style={{ fontWeight: 600, fontSize: 13 }}>{t.name}</td>
                  <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>{t.description}</td>
                  <td>
                    <span style={{
                      padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700,
                      background: t.category === 'system' ? '#EFF6FF' : '#FEF3C7',
                      color: t.category === 'system' ? '#1D4ED8' : '#92400E',
                    }}>
                      {t.category === 'system' ? 'System' : 'Allgemein'}
                    </span>
                  </td>
                  <td>
                    <span style={{
                      padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700,
                      background: t.updated_at ? '#d1fae5' : '#f3f4f6',
                      color: t.updated_at ? '#065f46' : '#6b7280',
                    }}>
                      {t.updated_at ? 'Angepasst' : 'Standard'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function TemplateEditor({ templateKey, onBack, apiFetch }) {
  const [template, setTemplate] = useState(null);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [showSource, setShowSource] = useState(false);
  const editorRef = useRef(null);

  const insertPlaceholder = (p) => {
    const editor = editorRef.current;
    if (editor) {
      editor.chain().focus().insertContent(`{{${p}}}`).run();
    }
  };

  useEffect(() => { loadTemplate(); }, [templateKey]);

  const loadTemplate = async () => {
    const r = await apiFetch(`/api/settings/mail-templates/${templateKey}`);
    if (!r) return;
    try {
      const d = await r.json();
      if (d) { setTemplate(d); setSubject(d.subject || ''); setBody(d.body || ''); }
    } catch {}
  };

  const showFb = (type, text) => { setFeedback({ type, text }); setTimeout(() => setFeedback(null), 5000); };

  const saveTemplate = async () => {
    setSaving(true);
    try {
      const r = await apiFetch(`/api/settings/mail-templates/${templateKey}`, {
        method: 'PUT', body: JSON.stringify({ subject, body }),
      });
      if (!r) { setSaving(false); return; }
      const d = await r.json();
      if (d?.status) showFb('success', 'Vorlage gespeichert.');
      else showFb('error', d?.detail || 'Fehler.');
    } catch (e) { showFb('error', e.message); }
    setSaving(false);
  };

  const resetTemplate = async () => {
    if (!window.confirm('Vorlage auf Standard zurücksetzen?')) return;
    const r = await apiFetch(`/api/settings/mail-templates/${templateKey}/reset`, { method: 'POST' });
    if (!r) return;
    try {
      const d = await r.json();
      if (d?.status) { showFb('success', 'Zurückgesetzt.'); loadTemplate(); }
    } catch {}
  };

  const deleteTemplate = async () => {
    if (!window.confirm('Vorlage unwiderruflich löschen?')) return;
    try {
      const r = await apiFetch(`/api/settings/mail-templates/${templateKey}`, { method: 'DELETE' });
      if (!r) return;
      const d = await r.json();
      if (d?.status) onBack();
    } catch {}
  };

  const isCustom = template?.custom;

  const sendTest = async () => {
    setTesting(true);
    try {
      const r = await apiFetch(`/api/settings/mail-templates/${templateKey}/test`, {
        method: 'POST', body: JSON.stringify({ recipient: 'claudio.gaertner@bgpr.org' }),
      });
      if (!r) { setTesting(false); return; }
      const text = await r.text();
      const d = JSON.parse(text);
      if (d?.status) showFb('success', d.message);
      else showFb('error', d?.detail || 'Fehler.');
    } catch (e) { showFb('error', 'SMTP-Fehler: ' + e.message); }
    setTesting(false);
  };

  if (!template) return <div className="empty-state"><p>Lade...</p></div>;

  return (
    <div data-testid="mail-template-editor">
      <div className="page-header">
        <button className="btn" onClick={onBack} style={{ background: 'none', border: 'none', padding: 0, marginBottom: 8, fontSize: 13, color: 'var(--text-secondary)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
          <ArrowLeft size={14} /> Zurück zur Liste
        </button>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <div>
            <h2>{template.name}</h2>
            <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>{template.description}</p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {!isCustom && (
              <button className="btn" onClick={resetTemplate} data-testid="template-reset-btn" style={{ background: 'var(--page-bg)', border: '1px solid var(--border)', fontSize: 13 }}>
                <RotateCcw size={14} /> Zurücksetzen
              </button>
            )}
            {isCustom && (
              <button className="btn" onClick={deleteTemplate} data-testid="template-delete-btn" style={{ background: '#FEE2E2', border: '1px solid #FECACA', fontSize: 13, color: '#991B1B' }}>
                <Trash2 size={14} /> Löschen
              </button>
            )}
            <button className="btn" onClick={sendTest} disabled={testing} data-testid="template-test-btn" style={{ background: 'var(--page-bg)', border: '1px solid var(--border)', fontSize: 13 }}>
              <Send size={14} /> {testing ? 'Sende...' : 'Test senden'}
            </button>
          </div>
        </div>
      </div>

      {feedback && <FeedbackBanner feedback={feedback} testId="template-feedback" />}

      {/* Placeholders info */}
      {template.placeholders?.length > 0 && (
        <PlaceholderBar placeholders={template.placeholders} onInsert={insertPlaceholder} />
      )}

      {/* Subject */}
      <div className="card" style={{ marginBottom: '1rem' }}>
        <div className="card-body">
          <label className="smtp-label">Betreff</label>
          <input className="form-input" value={subject} onChange={e => setSubject(e.target.value)} data-testid="template-subject-input" />
        </div>
      </div>

      {/* Body Editor */}
      <div className="card" style={{ marginBottom: '1rem' }}>
        <div className="card-body">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <label className="smtp-label" style={{ margin: 0 }}>Inhalt</label>
            <button type="button" onClick={() => setShowSource(!showSource)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 4 }} data-testid="template-toggle-source">
              <Code size={14} /> {showSource ? 'Editor' : 'Quellcode'}
            </button>
          </div>
          {showSource ? (
            <textarea className="form-input" value={body} onChange={e => setBody(e.target.value)} rows={12} style={{ fontFamily: 'monospace', fontSize: 13, resize: 'vertical' }} data-testid="template-source-textarea" />
          ) : (
            <TipTapEditor content={body} onChange={setBody} placeholder="E-Mail-Inhalt eingeben..." editorRef={editorRef} />
          )}
        </div>
      </div>

      <button className="btn btn-primary" onClick={saveTemplate} disabled={saving} data-testid="template-save-btn">
        <Save size={14} /> {saving ? 'Speichere...' : 'Vorlage speichern'}
      </button>
    </div>
  );
}

export default function MailTemplatePage() {
  const [templates, setTemplates] = useState([]);
  const [selectedKey, setSelectedKey] = useState(null);
  const { apiFetch } = useApi();

  useEffect(() => { loadTemplates(); }, []);

  const loadTemplates = async () => {
    const r = await apiFetch('/api/settings/mail-templates');
    if (!r) return;
    try { const d = await r.json(); if (Array.isArray(d)) setTemplates(d); } catch {}
  };

  if (selectedKey) {
    return <TemplateEditor templateKey={selectedKey} onBack={() => { setSelectedKey(null); loadTemplates(); }} apiFetch={apiFetch} />;
  }

  return <TemplateList templates={templates} onSelect={setSelectedKey} onRefresh={loadTemplates} apiFetch={apiFetch} />;
}
